import { Injectable } from '@angular/core';

interface ProductAnalitics {
  listName?: string;
  productIndex?: number;
}

type ProductCodeOrEntry = {
  entryNumber?: number;
  positionGuid?: string;
  product?: { code?: string };
};

@Injectable({ providedIn: 'root' })
export class ProductListCartAnalyticsService {
  private mapProductListName = new Map<string | number, ProductAnalitics | undefined>();

  saveAnalyticDataForEntry(entry: ProductCodeOrEntry, data?: ProductAnalitics): void {
    const key = this.getProductCodeOrEntry(entry);
    const existing = this.mapProductListName.get(key);

    this.mapProductListName.set(key, {
      listName: data?.listName ?? existing?.listName,
      productIndex: data?.productIndex ?? existing?.productIndex,
    });
  }

  getListNameByEntry(entry: ProductCodeOrEntry): string | undefined {
    return this.findAnalytics(entry)?.listName;
  }

  getProductIndexByEntry(entry: ProductCodeOrEntry): number | undefined {
    return this.findAnalytics(entry)?.productIndex;
  }

  updateMapForEntry(entry: ProductCodeOrEntry): void {
    const productCode = entry.product?.code;

    if (!productCode) {
      return;
    }

    const listName = this.getListNameByEntry(entry);
    const productIndex = this.getProductIndexByEntry(entry);
    const persistentKey = this.resolvePersistentKey(entry);

    if (persistentKey !== undefined) {
      this.mapProductListName.set(persistentKey, { listName, productIndex });
    }

    if (persistentKey !== productCode) {
      this.mapProductListName.delete(productCode);
    }
  }

  cleanMapAnalitycData(): void {
    this.mapProductListName = new Map<string | number, ProductAnalitics>();
  }

  getProductCodeOrEntry(entry: ProductCodeOrEntry): string | number {
    return entry.product?.code ?? this.resolvePersistentKey(entry) ?? '';
  }

  private findAnalytics(entry: ProductCodeOrEntry): ProductAnalitics | undefined {
    for (const key of this.getLookupKeys(entry)) {
      const data = this.mapProductListName.get(key);

      if (data) {
        return data;
      }
    }

    return undefined;
  }

  private getLookupKeys(entry: ProductCodeOrEntry): (string | number)[] {
    const keys: (string | number)[] = [];

    if (entry.entryNumber !== undefined && entry.entryNumber !== null) {
      keys.push(entry.entryNumber);
    }

    if (entry.positionGuid) {
      keys.push(entry.positionGuid);
    }

    if (entry.product?.code) {
      keys.push(entry.product.code);
    }

    return keys;
  }

  private resolvePersistentKey(entry: ProductCodeOrEntry): string | number | undefined {
    if (entry.entryNumber !== undefined && entry.entryNumber !== null) {
      return entry.entryNumber;
    }

    if (entry.positionGuid) {
      return entry.positionGuid;
    }

    return entry.product?.code;
  }
}
